/**
 * JU!CE — Sabores + Kit: add-to-cart e carrossel mobile.
 * Vanilla, sem libs. Progressive enhancement: os <form action="/cart/add">
 * funcionam sem JS (POST tradicional); com JS, <jp-add-to-cart> intercepta
 * e usa /cart/add.js via fetch.
 *
 * Integração com o Victory: quando window.themeCore.CartApi existe, usamos
 * CartApi.makeRequest(ADD_TO_CART / ADD_TO_CART_MANY, …) — é a mesma chamada
 * que o resto do tema usa (ver assets/component-product-card-with-form.build.js e
 * assets/main.build.js). Ela já faz o fetch para /cart/add.js e emite
 * window.themeCore.EventBus.emit('cart:updated', { items, action, params }),
 * que é o evento que assets/cart-notification.build.js escuta para abrir a
 * notificação de carrinho (snippets/cart-notification.liquid). Em seguida
 * chamamos GET_CART, que devolve item_count e é o que
 * assets/section-header.build.js usa para atualizar .js-header-cart-item-count-wrapper.
 * Sem themeCore (ou em outro tema), caímos num fetch direto + atualização
 * manual do contador do header.
 *
 * data-jp-items (14/09/2026, porte pra oferta): quando a <jp-add-to-cart> declara
 * esse atributo ("variant_id:qtd,variant_id:qtd,…" — ver sections/jp-pdp-bundles.liquid),
 * TODOS esses itens vão juntos via ADD_TO_CART_MANY, ignorando o id/quantity do form
 * (que só serve de fallback sem JS). Sem o atributo, mantém o comportamento antigo:
 * 1 item, na quantidade REAL do form (antes disso era sempre 1, mesmo quando o Liquid
 * mandava quantity=2/3 — causa raiz do bug "Dupla/Trio adiciona só um").
 *
 * data-jp-checkout="true": depois do add, chama window.yampiClick() (checkout Yampi)
 * em vez de deixar só o drawer nativo abrir. Sem window.yampiClick, comportamento
 * intacto (abre o drawer, como antes).
 */
(function () {
  'use strict';

  var DONE_MS = 1600;

  function getCartApi() {
    var core = window.themeCore;
    if (core && core.CartApi && typeof core.CartApi.makeRequest === 'function') {
      return core.CartApi;
    }
    return null;
  }

  function addItemsViaThemeCore(cartApi, items) {
    var action =
      items.length > 1 && cartApi.actions.ADD_TO_CART_MANY
        ? cartApi.actions.ADD_TO_CART_MANY
        : cartApi.actions.ADD_TO_CART;
    var payload = items.length > 1 ? items : items[0];

    return cartApi.makeRequest(action, payload).then(function (added) {
      // Atualiza o contador do header (o Victory escuta cart:updated com item_count).
      cartApi.makeRequest(cartApi.actions.GET_CART).catch(function () {});
      return added && added.items ? added.items : added;
    });
  }

  function addItemsFallback(items) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items })
    })
      .then(function (response) {
        if (!response.ok) {
          return response.json().then(function (err) {
            throw new Error((err && err.description) || 'cart_add_failed');
          });
        }
        return response.json();
      })
      .then(function (data) {
        var result = data && data.items ? data.items : data;
        updateCartCountFallback();
        return result;
      });
  }

  function updateCartCountFallback() {
    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var nodes = document.querySelectorAll(
          '.js-header-cart-item-count-wrapper, .js-cart-count, [data-cart-count]'
        );
        nodes.forEach(function (el) {
          el.setAttribute('data-cart-count', cart.item_count);
          if (!el.querySelector('*')) {
            el.textContent = cart.item_count;
          }
        });
      })
      .catch(function () {});
  }

  function addItemsToCart(items) {
    var cartApi = getCartApi();
    if (cartApi) {
      return addItemsViaThemeCore(cartApi, items);
    }
    return addItemsFallback(items);
  }

  /* "id1:qtd1,id2:qtd2,…" -> [{id, quantity}, …]. Entradas inválidas são ignoradas
     (nunca derrubam o resto do carrinho por causa de 1 item mal configurado). */
  function parseItemsAttr(raw) {
    if (!raw) return [];
    return raw
      .split(',')
      .map(function (part) {
        var pieces = part.split(':');
        var id = (pieces[0] || '').trim();
        var quantity = parseInt(pieces[1], 10);
        if (!id || !quantity || quantity < 1) return null;
        return { id: id, quantity: quantity };
      })
      .filter(Boolean);
  }

  function defineAddToCart() {
    if (customElements.get('jp-add-to-cart')) return;

    class JpAddToCartElement extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('[data-jp-cart-form]');
        this.button = this.querySelector('[data-jp-cart-submit]');
        this.label = this.querySelector('[data-jp-cart-label]');
        if (!this.form || !this.button) return;
        this._defaultLabel = this.label ? this.label.textContent : '';
        // Opt-in por atributo: só quem declara data-jp-redirect (hoje só o
        // "Comprar agora" dos cards de sabor da home) navega depois de
        // adicionar. jp-pdp-bundles e jp-cart não têm o atributo — comportamento
        // deles fica intacto.
        this._redirect = this.getAttribute('data-jp-redirect') || '';
        // "id1:qtd1,id2:qtd2,…" (ver sections/jp-pdp-bundles.liquid) — combo com
        // sabores diferentes vai tudo junto via ADD_TO_CART_MANY. Sem o atributo,
        // usa o id/quantity do próprio form (fallback também usado sem JS).
        this._items = this.getAttribute('data-jp-items') || '';
        // Depois do add, checkout direto (window.yampiClick()) em vez de só abrir
        // o drawer — hoje só os combos da PDP declaram isso.
        this._checkout = this.getAttribute('data-jp-checkout') === 'true';
        this._onSubmit = this.onSubmit.bind(this);
        this.form.addEventListener('submit', this._onSubmit);
      }

      disconnectedCallback() {
        if (this.form && this._onSubmit) {
          this.form.removeEventListener('submit', this._onSubmit);
        }
      }

      onSubmit(event) {
        event.preventDefault();
        if (this.button.hasAttribute('data-jp-busy')) return;

        var items = parseItemsAttr(this._items);

        if (!items.length) {
          var formData = new FormData(this.form);
          var id = formData.get('id');
          if (!id) return;
          var quantity = parseInt(formData.get('quantity'), 10);
          if (!quantity || quantity < 1) quantity = 1;
          items = [{ id: id, quantity: quantity }];
        }

        var self = this;
        this.setState('loading', 'Adicionando…');

        addItemsToCart(items)
          .then(function (result) {
            self.setState('done', 'Adicionado ✓');
            self.dispatchEvent(
              new CustomEvent('cart:item-added', {
                bubbles: true,
                detail: { items: items, result: result }
              })
            );

            if (self._checkout && typeof window.yampiClick === 'function') {
              // 250ms só dá tempo do "Adicionado ✓" ser percebido antes de sair —
              // sem isso o carrinho pisca "do nada" pro usuário.
              window.setTimeout(function () { window.yampiClick(); }, 250);
              return;
            }

            if (self._redirect) {
              window.setTimeout(function () {
                window.location.assign(self._redirect);
              }, 250);
              return;
            }
            window.setTimeout(function () { self.resetState(); }, DONE_MS);
          })
          .catch(function () {
            // Erro nunca redireciona — o usuário precisa ver "Tente de novo".
            self.setState('error', 'Tente de novo');
            window.setTimeout(function () { self.resetState(); }, DONE_MS);
          });
      }

      setState(state, text) {
        this.button.setAttribute('data-state', state);
        this.button.setAttribute('data-jp-busy', 'true');
        this.button.disabled = state === 'loading';
        if (this.label && text) this.label.textContent = text;
      }

      resetState() {
        this.button.removeAttribute('data-state');
        this.button.removeAttribute('data-jp-busy');
        this.button.disabled = false;
        if (this.label) this.label.textContent = this._defaultLabel;
      }
    }

    customElements.define('jp-add-to-cart', JpAddToCartElement);
  }

  function defineFlavorCarousel() {
    if (customElements.get('jp-flavor-carousel')) return;

    class JpFlavorCarouselElement extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-track]');
        this.dotsWrap = this.querySelector('[data-dots]');
        this.prevBtn = this.querySelector('[data-prev]');
        this.nextBtn = this.querySelector('[data-next]');
        if (!this.track) return;

        this.cards = Array.prototype.slice.call(this.track.children);
        this.buildDots();
        this.bindNav();
        this._onScroll = this.onScroll.bind(this);
        this.track.addEventListener('scroll', this._onScroll, { passive: true });
        this.onScroll();
      }

      disconnectedCallback() {
        if (this.track && this._onScroll) {
          this.track.removeEventListener('scroll', this._onScroll);
        }
      }

      buildDots() {
        if (!this.dotsWrap || this.cards.length < 2) return;
        this.dotsWrap.innerHTML = '';
        var self = this;
        this.dots = this.cards.map(function (card, index) {
          var dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'jp-sabores__dot';
          dot.setAttribute('role', 'tab');
          dot.setAttribute('aria-label', 'Ir para o sabor ' + (index + 1));
          dot.addEventListener('click', function () { self.scrollToIndex(index); });
          self.dotsWrap.appendChild(dot);
          return dot;
        });
      }

      bindNav() {
        var self = this;
        if (this.prevBtn) this.prevBtn.addEventListener('click', function () { self.step(-1); });
        if (this.nextBtn) this.nextBtn.addEventListener('click', function () { self.step(1); });
      }

      step(direction) {
        var first = this.cards[0];
        var width = first ? first.getBoundingClientRect().width + 16 : 0;
        this.track.scrollBy({ left: width * direction, behavior: 'smooth' });
      }

      scrollToIndex(index) {
        var card = this.cards[index];
        if (!card) return;
        this.track.scrollTo({
          left: card.offsetLeft - this.track.offsetLeft,
          behavior: 'smooth'
        });
      }

      onScroll() {
        if (!this.dots || !this.dots.length) return;
        var trackRect = this.track.getBoundingClientRect();
        var center = trackRect.left + trackRect.width / 2;
        var closest = 0;
        var closestDistance = Infinity;
        this.cards.forEach(function (card, index) {
          var rect = card.getBoundingClientRect();
          var cardCenter = rect.left + rect.width / 2;
          var distance = Math.abs(cardCenter - center);
          if (distance < closestDistance) {
            closestDistance = distance;
            closest = index;
          }
        });
        this.dots.forEach(function (dot, index) {
          dot.classList.toggle('is-active', index === closest);
          dot.setAttribute('aria-selected', index === closest ? 'true' : 'false');
        });
      }
    }

    customElements.define('jp-flavor-carousel', JpFlavorCarouselElement);
  }

  defineAddToCart();
  defineFlavorCarousel();
})();
